import React, {useMemo} from 'react';
import {Circle, Polygon, Tooltip} from 'react-leaflet';
import {useTranslation} from 'react-i18next';
import {
	useCloseStopsSingleVoyage
} from "@containers/singleVoyage/containers/closeStops/useCloseStopsSingleVoyage";
import {
	hasOwnRadiusSingleVoyage,
	ICloseStopsConflictSingleVoyage,
	MIN_STORED_RADIUS_SINGLE_VOYAGE,
	STAND_MARGIN_SINGLE_VOYAGE,
	storedRadiusSingleVoyage
} from "@containers/singleVoyage/containers/closeStops/findCloseStops";
import {buildZonesOverlapSingleVoyage} from "@containers/singleVoyage/containers/closeStops/closeStopsGeometry";
import {IWayPointCreationSingleVoyage} from "@containers/singleVoyage/types/point";

// error.main of the theme; leaflet paths take a plain colour
const CLOSE_STOPS_COLOR = '#d32f2f'

const ZONE_PATH = {
	color: CLOSE_STOPS_COLOR,
	weight: 2,
	dashArray: '6 6',
	fillOpacity: 0.05,
	interactive: false,
}

const OVERLAP_PATH = {
	color: CLOSE_STOPS_COLOR,
	weight: 1,
	fillColor: CLOSE_STOPS_COLOR,
	fillOpacity: 0.35,
}

const sx = {
	title: {fontWeight: 600, marginBottom: '4px'},
	line: {margin: 0},
	fix: {marginTop: '6px', fontWeight: 600},
}

interface IStop {
	point: IWayPointCreationSingleVoyage
	number: number
	radius: number
}

const OverlapTooltip = ({conflict, first, second}: {
	conflict: ICloseStopsConflictSingleVoyage
	first: IStop
	second: IStop
}) => {

	const {t} = useTranslation()
	const {distance, maxRadius} = conflict

	// The cause: a vehicle standing at one stop, at its centre or a little off it, is in the other zone
	const reached = [[first, second], [second, first]]
		.filter(([, outer]) => distance < outer.radius + STAND_MARGIN_SINGLE_VOYAGE)
	const insideLines = reached.length === 2 && first.radius === second.radius
		? [distance < first.radius
			? t('singleVoyage.closeStops.map.insideBoth', {depth: first.radius - distance})
			: t('singleVoyage.closeStops.map.nearBoth', {gap: distance - first.radius})]
		: reached.map(([inner, outer]) => distance < outer.radius
			? t('singleVoyage.closeStops.map.inside', {inner: inner.number, outer: outer.number, depth: outer.radius - distance})
			: t('singleVoyage.closeStops.map.near', {inner: inner.number, outer: outer.number, gap: distance - outer.radius}))

	const fixLines = maxRadius < MIN_STORED_RADIUS_SINGLE_VOYAGE
		? [t('singleVoyage.closeStops.fixMerge', {radius: MIN_STORED_RADIUS_SINGLE_VOYAGE})]
		: [
			...[first, second]
				.filter((stop) => stop.radius > maxRadius)
				.map((stop) => t(hasOwnRadiusSingleVoyage(stop.point)
					? 'singleVoyage.closeStops.map.fixRadius'
					: 'singleVoyage.closeStops.map.fixTemplateRadius', {
					number: stop.number,
					from: stop.radius,
					to: maxRadius,
				})),
			t('singleVoyage.closeStops.map.orMerge'),
		]

	return (
		<div>
			<div style={sx.title}>
				{t('singleVoyage.closeStops.map.title', {first: first.number, second: second.number})}
			</div>
			<p style={sx.line}>
				{t('singleVoyage.closeStops.map.sizes', {
					distance,
					first: first.number,
					second: second.number,
					radiusFirst: first.radius,
					radiusSecond: second.radius,
				})}
			</p>
			{insideLines.map((line) => <p key={line} style={sx.line}>{line}</p>)}
			<div style={sx.fix}>{t('singleVoyage.closeStops.map.fixTitle')}</div>
			{fixLines.map((line) => <p key={line} style={sx.line}>{line}</p>)}
		</div>
	)
}

const CloseStopsPairMap = ({conflict}: { conflict: ICloseStopsConflictSingleVoyage }) => {

	const first: IStop = {
		point: conflict.first,
		number: conflict.firstNumber,
		radius: storedRadiusSingleVoyage(conflict.first.radius),
	}
	const second: IStop = {
		point: conflict.second,
		number: conflict.secondNumber,
		radius: storedRadiusSingleVoyage(conflict.second.radius),
	}

	const overlap = useMemo(() => buildZonesOverlapSingleVoyage(
		first.point.geom.coordinates, first.radius,
		second.point.geom.coordinates, second.radius,
	), [first.point.geom, first.radius, second.point.geom, second.radius])

	const centre = ({point}: IStop): [number, number] => [point.geom.coordinates[1], point.geom.coordinates[0]]

	return (
		<>
			<Circle center={centre(first)} radius={first.radius} pathOptions={ZONE_PATH}/>
			<Circle center={centre(second)} radius={second.radius} pathOptions={ZONE_PATH}/>

			{overlap.length >= 3 && <Polygon positions={overlap} pathOptions={OVERLAP_PATH}>
                <Tooltip sticky direction={'top'}>
                    <OverlapTooltip conflict={conflict} first={first} second={second}/>
                </Tooltip>
            </Polygon>}
		</>
	)
}

/** Both zones of every too close pair and the area they share, which explains the conflict on hover. */
const CloseStopsMapSingleVoyage = () => {

	const {conflicts} = useCloseStopsSingleVoyage()

	return (
		<>
			{conflicts.map((conflict) => (
				<CloseStopsPairMap
					key={`${conflict.first.id}-${conflict.second.id}`}
					conflict={conflict}
				/>
			))}
		</>
	);
};

export default CloseStopsMapSingleVoyage;
